"use client";

import React from "react";
import Image from "next/image";
import SmoothImage from "@/components/SmoothImage";
import Link from "next/link";
import { useFetchNewsArticles } from "@/apis/queryFunctions/news";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import { Calendar } from "lucide-react";
import { renderTextWithBreaks } from "@/utils/stripHTML";

export interface NewsSectionData {
  tag?: string;
  title?: string;
  subtitle?: string;
  ctaText?: string;
  ctaHref?: string;
  readMoreText?: string;
}

interface NewsSectionProps {
  locale: string;
  data?: NewsSectionData;
  title?: string;
  subTitle?: string;
}

const formatDate = (dateString: string) => {
  if (!dateString) return "";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (e) {
    return dateString;
  }
};

export default function NewsSection({
  locale = "vi",
  data = {},
  title,
  subTitle,
}: NewsSectionProps) {
  // Fetch exactly 3 latest articles for fixed 3-column layout
  const { data: newsItems = [], isLoading } = useFetchNewsArticles({
    limit: 3,
    condition: { ngon_ngu: locale },
  });

  const sectionTag = subTitle || data?.tag || data?.subtitle || "08 · TIN TỨC & SỰ KIỆN";
  const sectionTitle = title || data?.title || "Bản tin dự án Dragon Home";
  const ctaText = data?.ctaText || "XEM TẤT CẢ TIN TỨC";
  const ctaHref = data?.ctaHref ? `/${locale}${data.ctaHref}` : `/${locale}/tin-tuc`;
  const readMoreText = data?.readMoreText || "Đọc chi tiết";

  if (isLoading) {
    return (
      <section
        id="tin-tuc"
        className="relative w-full h-auto lg:h-screen lg:min-h-screen flex flex-col justify-center overflow-hidden bg-[#FAF8F5] text-[#193B2F] px-6 sm:px-10 lg:px-14 xl:px-16 py-20"
      >
        <div className="max-w-7xl mx-auto flex justify-center items-center py-12">
          <div className="w-10 h-10 border-4 border-[#C9AB81] border-t-transparent rounded-full animate-spin" />
        </div>
      </section>
    );
  }

  if (!newsItems || newsItems.length === 0) {
    return null;
  }

  const articlesToShow = newsItems.slice(0, 3);

  return (
    <section
      id="tin-tuc"
      className="relative w-full h-auto lg:h-screen lg:min-h-screen lg:max-h-screen flex flex-col justify-center overflow-hidden bg-[#FAF8F5] text-[#193B2F] select-none px-6 sm:px-10 lg:px-14 xl:px-16 pt-12 sm:pt-16 lg:pt-20 pb-8 sm:pb-10 lg:pb-12"
    >
      {/* BACKGROUND TEXTURE OVERLAY */}
      <div className="absolute inset-0 z-0 pointer-events-none mix-blend-multiply opacity-60">
        <Image
          src="/images/background_2.png"
          alt="texture"
          fill
          sizes="100vw"
          priority
          className="object-cover object-center"
        />
      </div>

      {/* CENTERED CONTAINER */}
      <div className="relative z-10 w-full max-w-7xl mx-auto my-0 lg:my-auto flex flex-col justify-center gap-6 sm:gap-8 lg:gap-10">
        {/* ================= 1. HEADER ROW ================= */}
        <div className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 sm:gap-6 flex-shrink-0">
          {/* LEFT: TAG & MAIN TITLE */}
          <div className="w-full sm:w-[65%] space-y-1.5">
            <div className="flex items-center gap-3">
              <span className="w-7 h-[1px] bg-[#8A724C]" />
              <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-[#8A724C] font-sans">
                {sectionTag}
              </span>
            </div>

            <h2
              className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-lora font-normal text-[#193B2F] tracking-tight title-leading"
              style={{ lineHeight: 1.15 }}
            >
              {renderTextWithBreaks(sectionTitle)}
            </h2>
          </div>

          {/* RIGHT: CTA BUTTON */}
          <div className="w-full sm:w-[35%] flex flex-row items-center justify-start sm:justify-end flex-shrink-0">
            <Link
              href={ctaHref}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#E5D4B2] hover:bg-[#C9AB81] active:scale-98 text-[#193B2F] border border-[#C9AB81] font-sans text-xs font-bold uppercase tracking-wider transition-all duration-300 shadow-sm cursor-pointer"
            >
              <span>{ctaText}</span>
              <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                <path
                  d="M5 12h14M12 5l7 7-7 7"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </Link>
          </div>
        </div>

        {/* ================= 2. FIXED 3-COLUMN GRID ================= */}
        <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          {articlesToShow.map((item: any) => {
            const imageUrl = appendImageUrlFromAPI({
              src: item.picture || item.image,
              size: "xl",
            });
            return (
              <Link
                key={item._id || item.id}
                href={`/${locale}/tin-tuc/${item.slug}`}
                className="flex flex-col justify-between bg-[#FAF8F5] border border-[#193B2F]/10 hover:border-[#C9AB81] shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer group h-full"
              >
                <div>
                  {/* Cover image container */}
                  <div className="relative w-full aspect-[16/10] overflow-hidden bg-[#EAE5DB]">
                    <SmoothImage
                      src={imageUrl || "/images/map_ora.png"}
                      alt={item.title}
                      fill
                      sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                      className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    {item.loai_tin_tuc && (
                      <span className="absolute top-3 left-3 bg-[#E5D4B2] text-[#193B2F] font-sans font-bold text-[10px] uppercase tracking-wider px-2.5 py-1 shadow-sm">
                        {item.loai_tin_tuc}
                      </span>
                    )}
                  </div>

                  {/* Content area */}
                  <div className="p-5 sm:p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-1.5 text-[#8A724C] font-sans text-[11px] font-semibold uppercase tracking-wider mb-2">
                      <Calendar size={12} />
                      <span>{formatDate(item.date_created || item.date)}</span>
                    </div>

                    <h3 className="font-lora text-lg sm:text-xl text-[#193B2F] font-normal leading-snug group-hover:text-[#8A724C] transition-colors line-clamp-2 mb-2">
                      {item.title}
                    </h3>

                    <p className="font-sans text-xs text-[#193B2F]/70 line-clamp-3 leading-relaxed">
                      {item.summary ||
                        (item.content
                          ? item.content
                              .replace(/<[^>]*>/g, "")
                              .substring(0, 120) + "..."
                          : "")}
                    </p>
                  </div>
                </div>

                {/* Footer action link */}
                <div className="px-5 sm:px-6 pb-5 sm:pb-6 pt-3 border-t border-[#193B2F]/10 flex items-center justify-between">
                  <span className="text-[#8A724C] font-sans font-bold text-xs uppercase tracking-wider group-hover:translate-x-1.5 transition-transform duration-300 inline-flex items-center gap-1.5">
                    <span>{readMoreText}</span>
                    <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                      <path
                        d="M5 12h14M12 5l7 7-7 7"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
